import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SearchIcon, PenToolIcon, RocketIcon, GraduationCapIcon, HeadphonesIcon } from 'lucide-react';
const steps = [{
  number: '01',
  icon: SearchIcon,
  title: 'Discovery & Analysis',
  duration: 'Week 1',
  description: 'We analyze your current sales processes, identify bottlenecks, and define clear goals. You receive a detailed audit report with concrete recommendations for action.',
  deliverables: ['Process Audit', 'Gap Analysis', 'Goal Definition', 'Project Plan']
}, {
  number: '02',
  icon: PenToolIcon,
  title: 'Concept & Design',
  duration: 'Week 1-2',
  description: 'Based on the analysis, we develop your customized CRM concept. We define pipeline stages, automations, and integrations – all tailored to your way of working.',
  deliverables: ['Pipeline Design', 'Workflow Concept', 'Integration Plan', 'Data Model']
}, {
  number: '03',
  icon: RocketIcon,
  title: 'Implementation',
  duration: 'Week 2-3',
  description: 'We set up Close CRM, configure all automations, and migrate your existing data. Every integration is tested and documented.',
  deliverables: ['CRM Setup', 'Data Migration', 'Automations', 'Integrations']
}, {
  number: '04',
  icon: GraduationCapIcon,
  title: 'Training & Onboarding',
  duration: 'Week 3-4',
  description: 'Your team receives intensive training – not just on how to use it, but on best practices in sales. We ensure everyone uses the system effectively.',
  deliverables: ['Team Training', 'Video Tutorials', 'Documentation', 'Best Practices']
}, {
  number: '05',
  icon: HeadphonesIcon,
  title: 'Support & Optimization',
  duration: 'Ongoing',
  description: 'After go-live, we continue to be there for you. We monitor usage, optimize processes, and help with questions. Your success is our success.',
  deliverables: ['Ongoing Support', 'Monthly Reviews', 'Continuous Optimization', 'New Features']
}];
export function ProcessSteps() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" ref={ref} data-id="element-202">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-204">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-205">
            Our Process
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-206">
            5 steps to optimized sales
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-207">
            Our proven process guarantees a smooth implementation – from the
            initial analysis to ongoing operations.
          </p>
        </motion.div>

        <div className="relative" data-id="element-208">
          {/* Connection line */}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#0ea5e9] via-[#0ea5e9]/50 to-transparent hidden md:block" data-id="element-209" />

          <div className="space-y-12" data-id="element-210">
            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.15
          }} className={`relative flex flex-col lg:flex-row items-start gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-211">
                {/* Step number indicator */}
                <div className="absolute left-8 lg:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#0ea5e9] border-4 border-[#0c0c0c] hidden md:block" data-id="element-212" />

                {/* Content card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'}`} data-id="element-213">
                  <div className="p-8 rounded-2xl bg-[#1a1a1a] border border-white/5" data-id="element-214">
                    <div className="flex items-start gap-4 mb-6" data-id="element-215">
                      <div className="w-14 h-14 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0" data-id="element-216">
                        <step.icon className="w-7 h-7 text-[#0ea5e9]" data-id="element-217" />
                      </div>
                      <div data-id="element-218">
                        <div className="flex items-center gap-3 mb-1" data-id="element-219">
                          <span className="text-[#0ea5e9] font-mono font-bold" data-id="element-220">
                            {step.number}
                          </span>
                          <span className="text-xs text-gray-500 px-2 py-1 rounded-full bg-white/5" data-id="element-221">
                            {step.duration}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-white" data-id="element-222">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-400 leading-relaxed mb-6" data-id="element-223">
                      {step.description}
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-224">
                      {step.deliverables.map((deliverable, i) => <span key={i} className="px-3 py-1 text-xs font-medium text-[#0ea5e9] bg-[#0ea5e9]/10 rounded-full" data-id="element-225">
                          {deliverable}
                        </span>)}
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block flex-1" data-id="element-226" />
              </motion.div>)}
          </div>
        </div>

        {/* Timeline summary */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#0ea5e9]/10 to-[#06b6d4]/10 border border-[#0ea5e9]/20 text-center" data-id="element-227">
          <p className="text-xl lg:text-2xl text-white font-medium" data-id="element-228">
            <span className="text-[#0ea5e9]" data-id="element-229">Total duration:</span> 2-4 weeks
            until go-live –
            <span className="text-white font-bold" data-id="element-230"> 3x faster</span> than the
            industry average.
          </p>
        </motion.div>
      </div>
    </section>;
}